/**
 * Filtro specializzato per stringhe
 *
 * @description
 * StringFilter è ottimizzato per la gestione di valori stringa e fornisce
 * tutti gli operatori di ricerca testuale come contains, startsWith, endsWith.
 * È il filtro più comunemente usato per campi di testo e input di ricerca.
 */

import { BaseFilter } from '../core/base-filter';
import type { QueryParams } from '../types';

/**
 * Interfaccia per dati di filtro stringa
 * Utilizzabile per Vue 3 Composition API con oggetti plain reattivi
 *
 * @example
 * // Con factory function
 * const filter: IStringFilterData = createStringFilterData('name');
 */
export interface IStringFilterData {
	/** Nome della proprietà da filtrare */
	propertyName: string;
	/** Valore corrente del filtro */
	value: string | null;
	/** Valore di default del filtro */
	defaultValue: string | null;
	/** Nome della proprietà nella query string */
	queryPropertyName: string;
	/** Operatore di ricerca applicato */
	searchOperator: string;
	/** Se aggiornare la query string al cambio */
	updateQueryOnChange: boolean;
}

/**
 * Tipo combinato per filtro stringa con metodi fluenti
 * Utilizzabile per Vue 3 Composition API
 */
export type StringFilterFactory = IStringFilterData & {
	/** Imposta il valore iniziale */
	initialValue(value: string | null): StringFilterFactory;
	/** Imposta il valore di default */
	default(value: string | null): StringFilterFactory;
	/** Configura aggiornamento query string */
	updateQueryOnFilterChange(value: boolean): StringFilterFactory;
	/** Configura nome proprietà query string */
	setQueryPropertyName(value: string): StringFilterFactory;
	/** Operatore "contiene" */
	contains(): StringFilterFactory;
	/** Operatore "uguale" */
	equals(): StringFilterFactory;
	/** Operatore "inizia con" */
	startsWith(): StringFilterFactory;
	/** Operatore "termina con" */
	endsWith(): StringFilterFactory;
};

/**
 * Filtro per valori stringa
 *
 * @example
 * // Filtro per ricerca per nome
 * const nameFilter = new StringFilter('customer.name')
 *   .contains()  // Ricerca con operatore "contiene"
 *   .initialValue('Mario');
 *
 * @example
 * // Filtro per codice che inizia con prefisso
 * const codeFilter = new StringFilter('code')
 *   .startsWith()  // Operatore "inizia con"
 *   .default('')   // Valore di default vuoto
 *   .updateQueryOnFilterChange(true);
 *
 * @example
 * // Filtro per email con dominio specifico
 * const emailFilter = new StringFilter('user.email')
 *   .endsWith()    // Operatore "termina con"
 *   .queryPropertyName('userEmail');  // Nome personalizzato nell'URL
 *
 * @category Filters
 * @since 1.0.0
 */
export class StringFilter extends BaseFilter<string | null> {
	/**
	 * Crea una nuova istanza di StringFilter
	 *
	 * @param propertyName - Nome della proprietà da filtrare (supporta notazione con punto: 'customer.name')
	 *
	 * @example
	 * // Filtro semplice per nome
	 * const filter = new StringFilter('name');
	 *
	 * @example
	 * // Filtro per proprietà nidificata
	 * const filter = new StringFilter('order.customer.companyName');
	 *
	 * @public
	 */
	constructor(propertyName: string) {
		super(String, propertyName);
	}

	/**
	 * Deserializza il valore dalla query string
	 * I valori stringa vengono passati così come sono, senza conversioni speciali
	 *
	 * @param query - Parametri della query string
	 *
	 * @example
	 * // URL: ?customer_name=Mario%20Rossi
	 * filter._fromQuery({ customer_name: 'Mario Rossi' });
	 * // filter.value diventa 'Mario Rossi'
	 *
	 * @protected
	 * @override
	 */
	protected _fromQuery(query: QueryParams): void {
		const value = query[this._queryPropertyName];
		if (value !== undefined && value !== this.value) {
			// FIX: Gestisci 'null' letterale dalla query string
			if (value === 'null') {
				this.value = null;
				return;
			}

			this.value = typeof value === 'string' ? value : String(value);
		}
	}

	/**
	 * Serializza il valore per la query string
	 * Le stringhe vengono passate direttamente senza encoding aggiuntivo
	 *
	 * @returns Il valore stringa per l'URL
	 *
	 * @example
	 * filter.value = 'Mario Rossi';
	 * filter._getSerializedValue(); // 'Mario Rossi'
	 *
	 * @protected
	 * @override
	 */
	protected _getSerializedValue(): string | undefined {
		return this.value || undefined;
	}

	/**
	 * Verifica se il valore è quello di default
	 * Per le stringhe, considera anche le stringhe vuote come valori di default
	 *
	 * @returns true se il valore è di default, false altrimenti
	 *
	 * @example
	 * filter.value = '';
	 * filter._valueIsDefault(); // true (stringa vuota è considerata default)
	 *
	 * filter.value = 'test';
	 * filter._valueIsDefault(); // false
	 *
	 * @protected
	 * @override
	 */
	protected _valueIsDefault(): boolean {
		return (
			this.value === undefined ||
			this.value === null ||
			this.value === '' ||
			this.value === this._defaultValue
		);
	}
}

/**
 * Factory function per creare filtri stringa tipizzati
 * Compatibile con Vue 3 Composition API e completamente reattivo
 *
 * @param propertyName - Nome della proprietà da filtrare
 * @returns Oggetto filtro stringa tipizzato con API fluente
 *
 * @example
 * // Per Vue 3 Composition API
 * const descriptionFilter = ref(createStringFilterData('description').contains());
 *
 * @example
 * // Con configurazione completa
 * const nameFilter = ref(
 *   createStringFilterData('customer.name')
 *     .contains()
 *     .initialValue('')
 *     .updateQueryOnFilterChange(true)
 * );
 */
export function createStringFilterData(propertyName: string): StringFilterFactory {
	// Sanitizza nome proprietà per query string (rimuove punti e caratteri speciali)
	const sanitizedName = propertyName.replace(/[.[\]]/g, '_').toLowerCase();

	// Oggetto combinato con dati e metodi
	const filter: StringFilterFactory = {
		propertyName,
		value: null,
		defaultValue: null,
		queryPropertyName: sanitizedName,
		searchOperator: 'NOOP',
		updateQueryOnChange: false,

		initialValue(value: string | null): StringFilterFactory {
			filter.value = value;
			return filter;
		},

		default(value: string | null): StringFilterFactory {
			filter.defaultValue = value;
			return filter;
		},

		updateQueryOnFilterChange(value: boolean): StringFilterFactory {
			filter.updateQueryOnChange = value;
			return filter;
		},

		setQueryPropertyName(value: string): StringFilterFactory {
			filter.queryPropertyName = value;
			return filter;
		},

		contains(): StringFilterFactory {
			filter.searchOperator = 'CONTAINS';
			return filter;
		},

		equals(): StringFilterFactory {
			filter.searchOperator = 'EQUAL';
			return filter;
		},

		startsWith(): StringFilterFactory {
			filter.searchOperator = 'STARTS_WITH';
			return filter;
		},

		endsWith(): StringFilterFactory {
			filter.searchOperator = 'ENDS_WITH';
			return filter;
		},
	};

	return filter;
}
